import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
} from '../componentes/base';
import { AreaDeTexto } from '../componentes/campos';

interface Modelo {
  readonly chave: string;
  readonly titulo: string;
  readonly texto: string;
  readonly ativo: boolean;
}

export function PaginaModelos() {
  const clienteConsulta = useQueryClient();
  const [editando, setEditando] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState('');
  const [salvo, setSalvo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['modelos'],
    queryFn: () =>
      api.buscar<{ modelos: Modelo[]; variaveis: Record<string, string> }>('/cobranca/modelos'),
  });

  const salvar = useMutation({
    mutationFn: ({ chave, texto }: { chave: string; texto: string }) =>
      api.alterar(`/cobranca/modelos/${chave}`, { texto }),
    onSuccess: (_r, v) => {
      setErro(null);
      setEditando(null);
      setSalvo(v.chave);
      void clienteConsulta.invalidateQueries({ queryKey: ['modelos'] });
    },
    /* O editor fica ABERTO quando falha — fechá-lo perderia o texto digitado, que
       é o pior desfecho possível numa tela onde se escreve parágrafos. */
    onError: (e) => {
      setSalvo(null);
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível salvar o modelo.');
    },
  });

  if (isLoading || !data) return <Carregando />;

  return (
    <>
      <CabecalhoDaPagina
        titulo="Modelos de mensagem"
        descricao="Os textos que o devedor recebe. Editáveis aqui — nada disso está no código."
      />

      {erro && (
        <div className="mb-5">
          <Alerta tom="vencido">{erro}</Alerta>
        </div>
      )}

      <div className="mb-5">
        <Alerta>
          As variáveis entre chaves duplas são substituídas na hora do envio. Variável
          desconhecida vira texto vazio, nunca aparece <code>{'{{assim}}'}</code> na
          mensagem do devedor.
        </Alerta>
      </div>

      <Cartao className="mb-6">
        <h2 className="mb-3 text-sm font-medium text-[color:var(--texto)]">
          Variáveis disponíveis
        </h2>
        <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(data.variaveis).map(([chave, explicacao]) => (
            <p key={chave} className="text-xs">
              <code
                className="font-mono"
                style={{ color: 'var(--acento)' }}
              >{`{{${chave}}}`}</code>
              <span className="ml-1.5 text-[color:var(--texto-tenue)]">{explicacao}</span>
            </p>
          ))}
        </div>
      </Cartao>

      <div className="flex flex-col gap-4">
        {data.modelos.map((modelo) => {
          const emEdicao = editando === modelo.chave;
          return (
            <Cartao key={modelo.chave}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-base text-[color:var(--texto)]">{modelo.titulo}</h3>
                  {!modelo.ativo && <Ficha tom="neutro">Desativado</Ficha>}
                  {salvo === modelo.chave && <Ficha tom="recebido">Salvo</Ficha>}
                </div>
                {emEdicao ? (
                  <div className="flex gap-2">
                    <Botao onClick={() => setEditando(null)} className="px-2.5 py-1 text-xs">
                      Cancelar
                    </Botao>
                    <Botao
                      tipo="primario"
                      onClick={() => salvar.mutate({ chave: modelo.chave, texto: rascunho })}
                      desabilitado={salvar.isPending || rascunho.trim() === ''}
                      className="px-2.5 py-1 text-xs"
                    >
                      Salvar
                    </Botao>
                  </div>
                ) : (
                  <Botao
                    onClick={() => {
                      setEditando(modelo.chave);
                      setRascunho(modelo.texto);
                      setSalvo(null);
                    }}
                    className="px-2.5 py-1 text-xs"
                  >
                    Editar
                  </Botao>
                )}
              </div>

              {emEdicao ? (
                <AreaDeTexto valor={rascunho} aoMudar={setRascunho} linhas={12} fonteMono />
              ) : (
                <pre className="rounded border bg-[color:var(--superficie-afundada)] p-3 font-sans text-xs leading-relaxed whitespace-pre-wrap text-[color:var(--texto-suave)]">
                  {modelo.texto}
                </pre>
              )}
            </Cartao>
          );
        })}
      </div>
    </>
  );
}
